import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, Save, Send, Trash2, ClipboardList, CheckCircle2,
  AlertCircle, ChevronDown, ChevronRight, RefreshCw, FileText,
  User, Calendar, MessageSquare, List
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  sent: { label: 'Sent to Client', color: 'bg-blue-100 text-blue-700', icon: Send },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
};

const CATEGORY_ICONS = {
  Justification: '📋',
  Goals: '🎯',
  Deliverables: '📦',
  'Expected Results': '✨',
  Assumptions: '💭',
  Constraints: '⚠️',
  Inclusions: '✅',
  Exclusions: '❌',
  Additional: '💬',
};

export default function ClientSurveyPage() {
  const { projectId, surveyId } = useParams();
  const navigate = useNavigate();
  const isNew = !surveyId || surveyId === 'new'; // Handle both undefined and 'new'
  
  const [loading, setLoading] = useState(!isNew); // Start false for new survey
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [template, setTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Template selection for new survey
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [showTemplateDialog, setShowTemplateDialog] = useState(isNew); // Show immediately for new
  
  // Dark mode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const checkDarkMode = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };
    checkDarkMode();
    window.addEventListener('storage', checkDarkMode);
    return () => window.removeEventListener('storage', checkDarkMode);
  }, []);

  useEffect(() => {
    loadData();
  }, [projectId, surveyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load project
      const projectRes = await axios.get(`${API}/api/projects/${projectId}`);
      setProject(projectRes.data);
      
      // Load templates list
      const templatesRes = await axios.get(`${API}/api/survey-templates`);
      setTemplates(templatesRes.data);
      
      if (isNew) {
        setShowTemplateDialog(true);
        setLoading(false);
        return; // Early return for new survey - don't try to load existing survey
      }
      
      // Load existing survey only if we have a valid surveyId
      if (surveyId && surveyId !== 'new') {
        const surveyRes = await axios.get(`${API}/api/projects/${projectId}/surveys/${surveyId}`);
        setSurvey(surveyRes.data);
        
        // Load template questions
        const templateRes = await axios.get(`${API}/api/survey-templates/${surveyRes.data.survey_template}`);
        setTemplate(templateRes.data);
        
        // Initialize responses from existing data
        const existingResponses = {};
        (surveyRes.data.responses || []).forEach(r => {
          existingResponses[r.question_id] = r.answer;
        });
        setResponses(existingResponses);
        
        // Expand all categories by default
        const categories = {};
        templateRes.data.questions.forEach(q => {
          if (q.category) categories[q.category] = true;
        });
        setExpandedCategories(categories);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load survey data');
    } finally {
      setLoading(false);
    }
  };

  const createSurvey = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a survey template');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/projects/${projectId}/surveys`, {
        template_id: selectedTemplate
      });
      
      // Load the full template
      const templateRes = await axios.get(`${API}/api/survey-templates/${selectedTemplate}`);
      setTemplate(templateRes.data);
      setSurvey(response.data);
      setShowTemplateDialog(false);
      
      // Navigate to the new survey
      navigate(`/projects/${projectId}/surveys/${response.data.id}`, { replace: true });
      
      // Expand all categories
      const categories = {};
      templateRes.data.questions.forEach(q => {
        if (q.category) categories[q.category] = true;
      });
      setExpandedCategories(categories);
      
      toast.success('Survey created');
    } catch (error) {
      console.error('Error creating survey:', error);
      toast.error('Failed to create survey');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (questionId, value, questionText) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Format responses for API
      const formattedResponses = Object.entries(responses).map(([questionId, answer]) => {
        const question = template?.questions?.find(q => q.id === questionId);
        return {
          question_id: questionId,
          question_text: question?.question || '',
          answer: answer
        };
      });
      
      await axios.put(`${API}/api/projects/${projectId}/surveys/${survey.id}`, {
        responses: formattedResponses
      });
      
      toast.success('Survey saved');
    } catch (error) {
      console.error('Error saving survey:', error);
      toast.error('Failed to save survey');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      // Save responses first
      const formattedResponses = Object.entries(responses).map(([questionId, answer]) => {
        const question = template?.questions?.find(q => q.id === questionId);
        return {
          question_id: questionId,
          question_text: question?.question || '',
          answer: answer
        };
      });
      
      await axios.put(`${API}/api/projects/${projectId}/surveys/${survey.id}`, {
        responses: formattedResponses,
        status: 'completed',
        completed_by: project?.client_name || 'Client'
      });
      
      // Summarize the survey
      await axios.post(`${API}/api/projects/${projectId}/surveys/${survey.id}/summarize`);
      
      toast.success('Survey completed and summarized!');
      
      // Refresh data
      await loadData();
    } catch (error) {
      console.error('Error completing survey:', error);
      toast.error('Failed to complete survey');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateScope = async () => {
    try {
      const response = await axios.post(`${API}/api/projects/${projectId}/scope-statements/generate-from-survey`, {
        survey_id: survey.id
      });
      
      toast.success('Scope statement generated!');
      navigate(`/projects/${projectId}/scope-statements/${response.data.id}`);
    } catch (error) {
      console.error('Error generating scope:', error);
      toast.error('Failed to generate scope statement');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;
    
    try {
      await axios.delete(`${API}/api/projects/${projectId}/surveys/${survey.id}`);
      toast.success('Survey deleted');
      navigate(`/projects/${projectId}/hub?tab=documents`);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete survey');
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Group questions by category
  const questionsByCategory = template?.questions?.reduce((acc, q) => {
    const cat = q.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(q);
    return acc;
  }, {}) || {};

  const cardClass = darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-[#E5E2DD]';
  const inputClass = darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A] text-[#E5E2DD]' : '';
  const textClass = darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]';
  const mutedClass = darkMode ? 'text-[#8A8A8A]' : 'text-[#6B6965]';

  // Render question input based on type
  const renderQuestionInput = (question) => {
    const value = responses[question.id];
    
    switch (question.question_type) {
      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value, question.question)}
            placeholder={question.placeholder || 'Enter your response...'}
            rows={4}
            className={inputClass}
            data-testid={`survey-input-${question.id}`}
          />
        );
      
      case 'select':
        return (
          <Select 
            value={value || ''} 
            onValueChange={(v) => handleResponseChange(question.id, v, question.question)}
          >
            <SelectTrigger className={inputClass} data-testid={`survey-select-${question.id}`}>
              <SelectValue placeholder="Select an option..." />
            </SelectTrigger>
            <SelectContent>
              {question.options?.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'multiselect':
        return (
          <div className="space-y-2">
            {question.options?.map(opt => (
              <div key={opt} className="flex items-center gap-2">
                <Checkbox
                  id={`${question.id}-${opt}`}
                  checked={(value || []).includes(opt)}
                  onCheckedChange={(checked) => {
                    const currentValues = value || [];
                    if (checked) {
                      handleResponseChange(question.id, [...currentValues, opt], question.question);
                    } else {
                      handleResponseChange(question.id, currentValues.filter(v => v !== opt), question.question);
                    }
                  }}
                  data-testid={`survey-checkbox-${question.id}-${opt}`}
                />
                <Label htmlFor={`${question.id}-${opt}`} className={`text-sm ${textClass}`}>{opt}</Label>
              </div>
            ))}
          </div>
        );
      
      case 'radio':
        return (
          <RadioGroup 
            value={value || ''} 
            onValueChange={(v) => handleResponseChange(question.id, v, question.question)}
          >
            {question.options?.map(opt => (
              <div key={opt} className="flex items-center gap-2">
                <RadioGroupItem value={opt} id={`${question.id}-${opt}`} />
                <Label htmlFor={`${question.id}-${opt}`} className={`text-sm ${textClass}`}>{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={value || false}
              onCheckedChange={(checked) => handleResponseChange(question.id, checked, question.question)}
              data-testid={`survey-checkbox-${question.id}`}
            />
            <Label className={`text-sm ${textClass}`}>Yes</Label>
          </div>
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value, question.question)}
            placeholder={question.placeholder}
            className={`${inputClass} w-40`}
            data-testid={`survey-input-${question.id}`}
          />
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value, question.question)}
            className={`${inputClass} w-48`}
            data-testid={`survey-input-${question.id}`}
          />
        );
      
      default: // text
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleResponseChange(question.id, e.target.value, question.question)}
            placeholder={question.placeholder || 'Enter your response...'}
            className={inputClass}
            data-testid={`survey-input-${question.id}`}
          />
        );
    }
  };

  if (loading && !isNew) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F1ED]'}`}>
        <RefreshCw className="w-8 h-8 animate-spin text-[#BC5D41]" />
      </div>
    );
  }

  // For new surveys, show template selection
  if (isNew && !survey) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F1ED]'}`}>
        {/* Header */}
        <header className={`sticky top-0 z-50 ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white'} border-b px-6 py-4`}>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/projects/${projectId}/hub?tab=documents`)}
              className={darkMode ? 'hover:bg-[#1A1A1A]' : ''}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <HelpIcon elementId="client-survey" position="inline" />
              <div>
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-[#BC5D41]" />
                  <h1 className={`text-xl font-heading ${textClass}`}>New Client Survey</h1>
                </div>
                <p className={`text-sm ${mutedClass}`}>
                  {project?.name} • {project?.client_name || 'Client'}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Template Selection */}
        <div className="max-w-3xl mx-auto px-6 py-8">
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle className={textClass}>Select Survey Template</CardTitle>
              <CardDescription className={mutedClass}>
                Choose a survey template based on your project type
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {templates.length === 0 ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin text-[#BC5D41]" />
                    <p className={mutedClass}>Loading templates...</p>
                  </div>
                ) : templates.map(t => (
                  <div
                    key={t.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedTemplate === t.id
                        ? 'border-[#BC5D41] bg-[#BC5D41]/10'
                        : darkMode ? 'border-[#2A2A2A] hover:border-[#3A3A3A]' : 'border-[#E5E2DD] hover:border-[#D5D2CD]'
                    }`}
                    onClick={() => setSelectedTemplate(t.id)}
                    data-testid={`template-option-${t.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`font-medium ${textClass}`}>{t.name}</h4>
                        <p className={`text-sm ${mutedClass}`}>{t.description}</p>
                      </div>
                      <Badge variant="outline">{t.question_count} questions</Badge>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => navigate(`/projects/${projectId}/hub?tab=documents`)}>
                  Cancel
                </Button>
                <Button 
                  onClick={createSurvey}
                  disabled={!selectedTemplate || templates.length === 0}
                  className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                  data-testid="create-survey-btn"
                >
                  Create Survey
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[survey?.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  const isCompleted = survey?.status === 'completed';
  const answeredCount = Object.keys(responses).filter(k => {
    const val = responses[k];
    return val && (Array.isArray(val) ? val.length > 0 : true);
  }).length;
  const totalQuestions = template?.questions?.length || 0;
  const requiredQuestions = template?.questions?.filter(q => q.required) || [];
  const answeredRequired = requiredQuestions.filter(q => {
    const val = responses[q.id];
    return val && (Array.isArray(val) ? val.length > 0 : true);
  }).length;

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F1ED]'}`}>
      {/* Template Selection Dialog */}
      <Dialog open={showTemplateDialog === true} onOpenChange={setShowTemplateDialog}>
        <DialogContent className={`max-w-2xl ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={textClass}>Select Survey Template</DialogTitle>
            <DialogDescription className={mutedClass}>
              Choose a survey template based on your project type
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-3 py-4 max-h-[60vh] overflow-y-auto">
            {templates.length === 0 ? (
              <div className="text-center py-4">
                <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin text-[#BC5D41]" />
                <p className={mutedClass}>Loading templates...</p>
              </div>
            ) : templates.map(t => (
              <div
                key={t.id}
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedTemplate === t.id
                    ? 'border-[#BC5D41] bg-[#BC5D41]/10'
                    : darkMode ? 'border-[#2A2A2A] hover:border-[#3A3A3A]' : 'border-[#E5E2DD] hover:border-[#D5D2CD]'
                }`}
                onClick={() => setSelectedTemplate(t.id)}
                data-testid={`template-option-${t.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className={`font-medium ${textClass}`}>{t.name}</h4>
                    <p className={`text-sm ${mutedClass}`}>{t.description}</p>
                  </div>
                  <Badge variant="outline">{t.question_count} questions</Badge>
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => navigate(`/projects/${projectId}/hub?tab=documents`)}>
              Cancel
            </Button>
            <Button 
              onClick={createSurvey}
              disabled={!selectedTemplate || templates.length === 0}
              className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
              data-testid="create-survey-btn"
            >
              Create Survey
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className={`sticky top-0 z-50 ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white'} border-b px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/projects/${projectId}/hub?tab=documents`)}
              className={darkMode ? 'hover:bg-[#1A1A1A]' : ''}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#BC5D41]" />
                <h1 className={`text-xl font-heading ${textClass}`}>
                  {survey?.title || template?.name || 'Client Survey'}
                </h1>
                {survey && (
                  <Badge className={statusConfig.color}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                )}
              </div>
              <p className={`text-sm ${mutedClass}`}>
                {project?.name} • {project?.client_name || 'Client'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {survey && !isCompleted && (
              <>
                <Button 
                  variant="outline"
                  onClick={handleDelete}
                  className={`text-red-600 hover:text-red-700 ${darkMode ? 'border-[#2A2A2A]' : ''}`}
                  data-testid="delete-survey-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleSave}
                  disabled={saving}
                  className={darkMode ? 'border-[#2A2A2A]' : ''}
                  data-testid="save-survey-btn"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Draft
                </Button>
                <Button 
                  onClick={handleComplete}
                  disabled={saving || answeredRequired < requiredQuestions.length}
                  className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                  data-testid="complete-survey-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Complete Survey
                </Button>
              </>
            )}
            {isCompleted && (
              <Button 
                onClick={handleGenerateScope}
                className="bg-[#70170F] hover:bg-[#70170F]/90 text-white"
                data-testid="generate-scope-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Generate Scope Statement
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      {survey && template && (
        <div className={`border-b ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'bg-white'} px-6 py-3`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm ${mutedClass}`}>
              Progress: {answeredCount} of {totalQuestions} questions answered
            </span>
            <span className={`text-sm ${mutedClass}`}>
              {requiredQuestions.length > 0 && `Required: ${answeredRequired}/${requiredQuestions.length}`}
            </span>
          </div>
          <div className={`h-2 rounded-full ${darkMode ? 'bg-[#2A2A2A]' : 'bg-[#E5E2DD]'}`}>
            <div 
              className="h-2 rounded-full bg-[#BC5D41] transition-all"
              style={{ width: `${totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      {survey && template && (
        <div className="max-w-4xl mx-auto px-6 py-6 space-y-4">
          {/* Survey Info */}
          <Card className={cardClass}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${darkMode ? 'bg-[#2A2A2A]' : 'bg-[#FAF9F7]'}`}>
                  <ClipboardList className="w-6 h-6 text-[#BC5D41]" />
                </div>
                <div className="flex-1">
                  <h3 className={`font-medium ${textClass}`}>{template.name}</h3>
                  <p className={`text-sm ${mutedClass}`}>{template.description}</p>
                  {isCompleted && (
                    <p className={`text-sm text-green-600 mt-2`}>
                      ✓ Completed by {survey.completed_by} on {new Date(survey.completed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Questions by Category */}
          {Object.entries(questionsByCategory).map(([category, questions]) => (
            <Collapsible 
              key={category} 
              open={expandedCategories[category]} 
              onOpenChange={() => toggleCategory(category)}
            >
              <Card className={cardClass}>
                <CollapsibleTrigger className="w-full">
                  <CardHeader className="flex flex-row items-center justify-between py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{CATEGORY_ICONS[category] || '📝'}</span>
                      <CardTitle className={`text-lg ${textClass}`}>{category}</CardTitle>
                      <Badge variant="outline" className="ml-2">
                        {questions.filter(q => responses[q.id]).length}/{questions.length}
                      </Badge>
                    </div>
                    {expandedCategories[category] ? (
                      <ChevronDown className={`w-5 h-5 ${mutedClass}`} />
                    ) : (
                      <ChevronRight className={`w-5 h-5 ${mutedClass}`} />
                    )}
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-6 pt-0">
                    {questions.map((question, idx) => (
                      <div key={question.id} className="space-y-2">
                        <Label className={`flex items-start gap-2 ${textClass}`}>
                          <span className={`text-sm ${mutedClass}`}>{idx + 1}.</span>
                          <span>
                            {question.question}
                            {question.required && <span className="text-red-500 ml-1">*</span>}
                          </span>
                        </Label>
                        {question.help_text && (
                          <p className={`text-xs ${mutedClass} ml-5`}>{question.help_text}</p>
                        )}
                        <div className="ml-5">
                          {renderQuestionInput(question)}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}

          {/* Survey Summary (for completed surveys) */}
          {isCompleted && survey.justification && (
            <Card className={cardClass}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${textClass}`}>
                  <FileText className="w-5 h-5 text-[#BC5D41]" />
                  Survey Summary
                </CardTitle>
                <CardDescription className={mutedClass}>
                  Extracted insights ready for scope statement
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {survey.justification && (
                  <div>
                    <Label className={mutedClass}>Justification</Label>
                    <p className={`text-sm ${textClass} mt-1`}>{survey.justification}</p>
                  </div>
                )}
                {survey.goals?.length > 0 && (
                  <div>
                    <Label className={mutedClass}>Goals</Label>
                    <ul className={`text-sm ${textClass} mt-1 list-disc list-inside`}>
                      {survey.goals.map((g, i) => <li key={i}>{g}</li>)}
                    </ul>
                  </div>
                )}
                {survey.deliverables?.length > 0 && (
                  <div>
                    <Label className={mutedClass}>Deliverables</Label>
                    <ul className={`text-sm ${textClass} mt-1 list-disc list-inside`}>
                      {survey.deliverables.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  </div>
                )}
                {survey.constraints?.length > 0 && (
                  <div>
                    <Label className={mutedClass}>Constraints</Label>
                    <ul className={`text-sm ${textClass} mt-1 list-disc list-inside`}>
                      {survey.constraints.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
